<template>
    <div ref="$el">
        <div class="flex justify-between items-start gap-2">
            <div class="font-semibold">
                {{ packStation.name }}
            </div>
            <div class="shrink-0">
                <span class="badge badge-primary badge-sm">{{ Math.round(packStation.distance / 1000).toFixed(1) }} km</span>
            </div>
        </div>
        <div class="mt-2">
            {{ packStation.place.address.streetAddress }}
        </div>
        <div>{{ packStation.place.address.postalCode }} {{ packStation.place.address.addressLocality }}</div>

        <div v-if="packStation.place.containedInPlace != null" class="text-base-content-subtle mt-2">
            {{ packStation.place.containedInPlace.name }}
        </div>

        <template v-if="isOpenAllTheTime(packStation.openingHours)">
            <div class="font-weight-bold text-success mt-2">
                {{ $t('packStation.openAllDay') }}
            </div>
        </template>
        <template v-else>
            <div
                v-for="(days, timeRange, index) in daysToTimes(packStation.openingHours)"
                :key="`${packStation.location.keywordId}${index}`"
                class="mt-1"
            >
                <span class="font-bold">{{ timeRange }}</span>:
                <span v-for="day in days" :key="day">
                    {{ day }}
                </span>
            </div>
        </template>

        <div
            v-for="(closure, index) in packStation.closurePeriods"
            :key="index"
            class="font-semibold text-error mt-2"
        >
            {{ $t('packStation.closed') }}: {{ formatDate(closure.fromDate) }}
            - {{ formatDate(closure.toDate) }}
        </div>

        <button
            type="button"
            class="btn btn-primary btn-outline mt-2 w-full"
            @click.prevent="selectPackStation()"
        >
            {{ $t('packStation.selectPackStation') }}
        </button>
    </div>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'
import type { PackStation } from '@/types/PackStation'

const props = defineProps<{
    packStation: PackStation
}>()

const emits = defineEmits(['stationSelected'])

const customerAddressFormData: Ref<Schemas['CustomerAddress']> | undefined = inject('customerAddressFormData')

const { t } = useI18n()
const attrs = useAttrs()
const $el = ref<HTMLElement>()

defineExpose({
    $el,
    id: attrs.id,
})

function isOpenAllTheTime (openingHours: { dayOfWeek: string; opens: string; closes: string }[]): boolean {
    const coveredDays: Record<string, boolean> = {}

    openingHours.map((period) => {
        if ('00:00:00' === period.opens && '23:59:00' === period.closes) {
            coveredDays[period.dayOfWeek] = true
        }
    })

    return Object.keys(coveredDays).length === 7
}

function daysToTimes (openingHours: { dayOfWeek: string; opens: string; closes: string }[]): Record<string, string> {
    const timesToDays: Record<string, string> = {}
    openingHours.map((period) => {
        const openingTime = new Date('1970-01-01T'+period.opens)
        const formattedOpeningTime = `${openingTime.getHours().toString().padStart(2, '0')}:${openingTime.getMinutes().toString().padStart(2, '0')}`
        const closingTime = new Date('1970-01-01T'+period.closes)
        const formattedClosingTime = `${closingTime.getHours().toString().padStart(2, '0')}:${closingTime.getMinutes().toString().padStart(2, '0')}`
        const timeRange = `${formattedOpeningTime} - ${formattedClosingTime}`
        const dayName = t(`packStation.days.${period.dayOfWeek.toLowerCase().replace('http://schema.org/', '')}`)
        if (timeRange in timesToDays) {
            timesToDays[timeRange] = `${timesToDays[timeRange]}, ${dayName}`
        } else {
            timesToDays[timeRange] = dayName
        }
    })

    return timesToDays
}

function formatDate (date: string): string {
    const dateObj = new Date(date)
    return `${dateObj.getDate()}.${dateObj.getMonth() + 1}.${dateObj.getFullYear()}`
}

function selectPackStation (): void {
    // set address data
    if (customerAddressFormData?.value != null) {
        customerAddressFormData.value.street = `${props?.packStation?.location?.keyword} ${props?.packStation?.location?.keywordId}`
        customerAddressFormData.value.zipcode = props?.packStation?.place?.address?.postalCode
        customerAddressFormData.value.city = props?.packStation?.place?.address?.addressLocality
    }

    emits('stationSelected')
}
</script>
